import { InvalidUrlError } from '@execonline-inc/url';
import { AppyError } from '../../../../../../Appy';
import { MissingLinkError } from '../../../../../../LinkyLinky';
import { SearchParameters } from '../../../../../../ListFilter/Types';
import { Link, Resource, ResourceWithMetadata } from '../../../../../../Resource/Types';
import { ExperienceBase } from '../../../../Types';
import { LearningCollectionEditFormResource } from '../../../Edit/Store/Types';
import { toTask, when } from '@execonline-inc/maybe-adapter';
import { equals } from '@execonline-inc/collections';
import Store from './Store';
import LearningCollectionEditStore from '../../../Edit/Store';
import { ModifiableExperiencesContext } from '../../../Types';
import { fromEmpty } from 'maybeasy';

export interface FilterOptionData {
  value: string;
  label: string;
  count: number;
}

export interface Experience extends Omit<ExperienceBase, 'displayOrder'> {}

export interface Metadata {
  resultsCount: number;
}

export type ExperienceResource = Resource<Experience>;
export type ShowUnlicensedToggle = 'show' | 'hide';
export type Toggle = 'show-licensed' | 'show-licensed-and-unlicensed';

export interface ExperiencesWithFilterOptions {
  experiences: ExperienceResource[];
  productTypeFilterOptions: FilterOptionData[];
  durationFilterOptions: FilterOptionData[];
  competencyFilterOptions: FilterOptionData[];
  schoolPartnerFilterOptions: FilterOptionData[];
  availableLanguagesFilterOptions: FilterOptionData[];
  showUnlicensedToggle: ShowUnlicensedToggle;
}
export type ExperiencesResource = ResourceWithMetadata<ExperiencesWithFilterOptions, Metadata>;

export interface HasExperiences {
  experiences: ExperiencesResource;
}

export const sortFilterSelectOptions = [
  {
    value: 'trending-desc',
    content: 'Trending First',
  },
  {
    value: 'title-asc',
    content: 'Title A to Z',
  },
  {
    value: 'title-desc',
    content: 'Title Z to A',
  },
];

export type SortOptionValue = 'title-asc' | 'title-desc' | 'trending-desc';

export interface Waiting {
  kind: 'waiting';
}

export interface WaitingWithLink {
  kind: 'waiting-with-link';
  link: Link;
  sortParameters: SearchParameters;
  searchParameters: SearchParameters;
}

export interface Loading {
  kind: 'loading';
  link: Link;
  sortParameters: SearchParameters;
  searchParameters: SearchParameters;
}

export type LoadError = AppyError | MissingLinkError | InvalidUrlError;

export interface LoadingError {
  kind: 'loading-error';
  link: Link;
  error: LoadError;
}

export interface Showing extends HasExperiences {
  kind: 'showing';
  link: Link;
  sortParameters: SearchParameters;
  searchParameters: SearchParameters;
}

export interface Hiding extends HasExperiences {
  kind: 'hiding';
  link: Link;
  sortParameters: SearchParameters;
  searchParameters: SearchParameters;
}

export interface SelectingPrograms extends HasExperiences {
  kind: 'selecting';
  link: Link;
  sortParameters: SearchParameters;
  searchParameters: SearchParameters;
  experienceResource: ExperienceResource;
  selectedIds: number[];
}

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const waitingWithLink = (link: Link): WaitingWithLink => ({
  kind: 'waiting-with-link',
  link,
  sortParameters: [
    {
      value: 'trending-desc',
      key: 'sort',
    },
  ],
  searchParameters: [],
});

export const loading = (state: WaitingWithLink): Loading => ({
  kind: 'loading',
  link: state.link,
  searchParameters: state.searchParameters,
  sortParameters: state.sortParameters,
});

export const loadingError = (link: Link, error: LoadError): LoadingError => ({
  kind: 'loading-error',
  link,
  error,
});

export const showing = (
  experiences: ExperiencesResource,
  state: Loading | Hiding | SelectingPrograms,
): Showing => ({
  kind: 'showing',
  experiences,
  link: state.link,
  searchParameters: state.searchParameters,
  sortParameters: state.sortParameters,
});

export const hiding = (
  experiences: ExperiencesResource,
  state: Showing | SelectingPrograms,
): Hiding => ({
  kind: 'hiding',
  experiences,
  link: state.link,
  searchParameters: state.searchParameters,
  sortParameters: state.sortParameters,
});

export const selectScheduledPrograms = (
  state: Showing,
  experienceResource: ExperienceResource,
  selectedIds: Array<number>,
): SelectingPrograms => ({
  ...state,
  kind: 'selecting',
  experienceResource,
  selectedIds: selectedIds,
});

export const selectProgram = (selectedIds: number[], state: SelectingPrograms) => ({
  ...state,
  selectedIds,
});

export interface CheckLicensedError {
  kind: 'program-is-licensed';
}

export interface WarningStatusError {
  kind: 'warning-status-hide';
}

export type ProgramError = CheckLicensedError | WarningStatusError;

export const programLicensedTask = (resource: ExperienceResource) =>
  toTask<ProgramError, {}>(
    { kind: 'program-is-licensed' },
    when(equals(resource.payload.status, 'Unlicensed'), {}),
  );

export const warningModelTask = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
) =>
  toTask<ProgramError, {}>(
    { kind: 'warning-status-hide' },
    when(equals(learningCollectionEditFormResource.payload.warningStatus, 'show'), {}),
  );

export const addProgramTask = (
  resource: ExperienceResource,
  store: Store,
  learningCollectionStore: LearningCollectionEditStore,
  context: ModifiableExperiencesContext,
) =>
  toTask(
    { kind: 'No scheduled program is available' },
    fromEmpty(resource.payload.programInstanceOptions.scheduled),
  ).fork(
    () => learningCollectionStore.addExperienceStore(context, resource),
    () => store.selectScheduledPrograms(resource),
  );

export type State =
  | Waiting
  | WaitingWithLink
  | Loading
  | LoadingError
  | Showing
  | Hiding
  | SelectingPrograms;
